import { logger } from '../../../modules/utils/apilogger';
import { numberWithCommas } from '../../../modules/utils/numericalFormatsHelper';
/**
 * Formats currency amount using proper locale and currency formatting
 * @param certificateBalance - The numeric balance amount
 * @param cardCurrency - The currency code (e.g., "USD", "EUR")
 * @param locale - Optional locale for formatting (defaults to 'en-US')
 * @returns Formatted currency string
 */
export const formatCurrency = (certificateBalance: number, cardCurrency: string, locale: string = 'en-US'): string => {
  try {
    // Use the cardCurrency (alphabetic code) for Intl.NumberFormat
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: cardCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(certificateBalance);
  } catch (error) {
    // Fallback to simple formatting if Intl.NumberFormat fail
    const { log } = logger({})('formatCurrency');

    log.warn(`Currency formatting failed for ${cardCurrency}`, error);

    return `${numberWithCommas(certificateBalance.toFixed(2))} ${cardCurrency}`;
  }
};

/**
 * Extracts currency symbol, value, and currency code for custom styling
 * @param certificateBalance - The numeric balance amount
 * @param cardCurrency - The currency code (e.g., "USD", "EUR")
 * @param locale - Optional locale for formatting (defaults to 'en-US')
 * @returns Object with symbol, value, and currencyCode for separate styling
 */
export const parseCurrencyComponents = (
  certificateBalance: number,
  cardCurrency: string,
  locale: string = 'en-US'
): {
  symbol: string;
  value: string;
  currencyCode: string;
} => {
  try {
    // Get currency symbol

    // Format just the numeric value without currency
    const numberFormatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const numericValue = numberFormatter.format(certificateBalance);

    return {
      symbol: '$',
      value: numericValue,
      currencyCode: cardCurrency,
    };
  } catch (error) {
    const { log } = logger({})('parseCurrencyComponents');
    log.warn(`Currency parsing failed for ${cardCurrency}:`, error);
    return {
      symbol: '$',
      value: numberWithCommas(certificateBalance?.toFixed(2)),
      currencyCode: cardCurrency,
    };
  }
};

/**
 * Comprehensive currency formatter that handles both scenarios and returns styled components:
 * - When you have both cardCurrency and cardCurrencyCode
 * - When you only have cardCurrencyCode and need to derive cardCurrency
 * Returns separate components for custom styling: symbol (small), value (large), currencyCode (small)
 */
export const formatGiftCardCurrency = (
  certificateBalance: number,
  cardCurrency?: string,
  locale: string = 'en-US'
): {
  symbol: string;
  value: string;
  currencyCode: string;
  fullFormatted: string; // Keep this for backward compatibility
} => {
  const resolvedCurrency = cardCurrency;

  if (resolvedCurrency) {
    const components = parseCurrencyComponents(certificateBalance, resolvedCurrency, locale);
    return {
      ...components,
      fullFormatted: formatCurrency(certificateBalance, resolvedCurrency, locale),
    };
  }

  // Fallback if neither is available
  return {
    symbol: '$',
    value: numberWithCommas(certificateBalance?.toFixed(2)),
    currencyCode: '',
    fullFormatted: numberWithCommas(certificateBalance?.toFixed(2)),
  };
};
